import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import type { CSSProperties } from 'react';
import {
  type FC,
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useCalendarTypes } from '~/src/hooks/useCalendarTypes';
import { useOpenMeetingLink } from '~/src/hooks/useOpenMeetingLink';
import { calendarService } from '~/src/services/calendarService';
import type { CalendarAttendee, CalendarEvent, CalendarRecentPanelProps } from '~/src/types/api';
// Asset imports - bundler handles path resolution
import calendarPlusIcon from '~/static/assets/icons/landing-page/calendar-plus.svg';
import calendarPlusIconDark from '~/static/assets/icons/landing-page/calendar-plus-dark.svg';
import checkIconGreen from '~/static/assets/icons/landing-page/check-icon-green.svg';
import copyIcon from '~/static/assets/icons/landing-page/copy-icon.svg';
import editIcon from '~/static/assets/icons/landing-page/edit-icon.svg';
import gcalendarIcon from '~/static/assets/icons/landing-page/gcalendar_icon.svg';
import outlookIcon from '~/static/assets/icons/landing-page/outlook_icon.svg';
import plusIcon from '~/static/assets/icons/landing-page/plus.svg';
import type { CreateMeetingParams } from '../../contexts';
import { useScheduledMeetingsMutations, useUser } from '../../contexts';
import {
  buildCalendarOauthCloseUrl,
  type CalendarProvider,
  isTrustedCalendarMessageOrigin,
} from '../../lib/calendarOauth';
import { openCalendarIntegrations } from '../../lib/openCalendarIntegrations';
import { isHostedByTeamHub } from '../../services/hostAuthService';
import { formatEventTime } from '../../utils/calendarEventUtils';
import { getMeetingUrl } from '../../utils/environment';
import { createMeetingAccessPayload } from '../../utils/meetingAccessPayload';
import { isPlainLeftClick } from '../../utils/meetingLinkRouting';
import type { CreatedCalendarEvent } from '../ScheduleMeetingModal/types';
import { automaticEventColorCycle } from './constants/calendarEventConstants';
import {
  filterEventsByTime,
  getAttendeeImage,
  getEventColors,
  getEventLocalStart,
  getLocalDateKey,
  getLocationLink,
  getOrganizerEmail,
  getOrganizerOrAttendee,
  hasExplicitEventColor,
  toLocalDateTime,
  withMicrosoftProvider,
} from './helpers/calendarEventHelpers';
import { redirectToCalendarEdit } from './helpers/redirectToCalendarEdit';
import { useCalendarAuthErrorRecovery } from './hooks/useCalendarAuthErrorRecovery';
import { useCalendarProviderReset } from './hooks/useCalendarProviderReset';
import { useFilterSelectedEvents } from './hooks/useFilterSelectedEvents';
import { useIncrementalCalendarSync } from './hooks/useIncrementalCalendarSync';
import { useLoadMoreCalendarEvents } from './hooks/useLoadMoreCalendarEvents';
import { useMergeEventsWithDelta } from './hooks/useMergeEventsWithDelta';

const CalendarRecentPanel: FC<CalendarRecentPanelProps> = ({
  activeTab = 'upcoming',
  searchInput = '',
  filterInput = 'all',
  onEventsProvider,
  onCalendarUpdateMeetingError,
  onCalendarFetchAuthError,
}) => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState<boolean | null>(null);
  const [_provider, setProvider] = useState<'google' | 'microsoft' | null>(null);
  const [error, _setError] = useState<string | null>(null);
  const [copiedEventId, setCopiedEventId] = useState<string | null>(null);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [googleEvents, setGoogleEvents] = useState<CalendarEvent[]>([]);
  const [microsoftEvents, setMicrosoftEvents] = useState<CalendarEvent[]>([]);
  const [googleNextPageToken, setGoogleNextPageToken] = useState<string | undefined>();
  const [microsoftNextLink, setMicrosoftNextLink] = useState<string | undefined>();
  const [isLoadingMoreGoogle, setIsLoadingMoreGoogle] = useState(false);
  const [isLoadingMoreMicrosoft, setIsLoadingMoreMicrosoft] = useState(false);
  const [timeBoundaryTick, setTimeBoundaryTick] = useState(0);
  const googleCacheRef = useRef<
    Record<string, { events: CalendarEvent[]; nextPageToken?: string; isBackfilled?: boolean }>
  >({});
  const microsoftCacheRef = useRef<
    Record<string, { events: CalendarEvent[]; nextLink?: string; isBackfilled?: boolean }>
  >({});
  const googleSyncTokenRef = useRef<Record<string, string | undefined>>({});
  const microsoftDeltaLinkRef = useRef<Record<string, string | undefined>>({});
  const autoLoadInProgressRef = useRef(false);
  const authErrorShownRef = useRef<{ google: boolean; microsoft: boolean }>({
    google: false,
    microsoft: false,
  });
  const previousTabRef = useRef<string | undefined>(undefined);
  const queryClient = useQueryClient();
  const [pendingCreatedCalendarEvent, setPendingCreatedCalendarEvent] = useState<
    CreatedCalendarEvent | undefined
  >(undefined);
  const [googleReconnectError, setGoogleReconnectError] = useState<string | null>(null);

  const { create, remove } = useScheduledMeetingsMutations();
  const { userEmail } = useUser();
  const openMeetingLink = useOpenMeetingLink();
  // Fetch Account Connections
  const {
    data: accountConnections,
    refetch,
    isError: isErrorConnections,
    isLoading: isConnectionsLoading,
    isFetching: isConnectionsFetching,
  } = useCalendarTypes();

  const { reportCalendarAuthError } = useCalendarAuthErrorRecovery({
    activeTab,
    refetchConnections: refetch,
    authErrorShownRef,
    googleSyncTokenRef,
    googleCacheRef,
    microsoftDeltaLinkRef,
    microsoftCacheRef,
    setGoogleReconnectError,
    setGoogleEvents,
    setGoogleNextPageToken,
    setMicrosoftEvents,
    setMicrosoftNextLink,
    onCalendarFetchAuthError,
  });

  const { resetProviderEvents } = useCalendarProviderReset({
    accountConnections,
    isConnectionsLoading,
    queryClient,
    googleCacheRef,
    microsoftCacheRef,
    googleSyncTokenRef,
    microsoftDeltaLinkRef,
    setGoogleEvents,
    setGoogleNextPageToken,
    setMicrosoftEvents,
    setMicrosoftNextLink,
  });

  // Drop stale reconnect messaging when a provider is no longer connected (sessionStorage
  // survives refresh; silent disconnect-before-reconnect does not post calendar-disconnected).
  useEffect(() => {
    if (isConnectionsLoading || !accountConnections) {
      return;
    }

    if (accountConnections.google?.connected) {
      try {
        setGoogleReconnectError(sessionStorage.getItem('calendarReconnectError:google'));
      } catch {
        setGoogleReconnectError(null);
      }
    } else {
      authErrorShownRef.current.google = false;
      setGoogleReconnectError(null);
      sessionStorage.removeItem('calendarReconnectError:google');
    }

    if (!accountConnections.microsoft?.connected) {
      authErrorShownRef.current.microsoft = false;
      sessionStorage.removeItem('calendarReconnectError:microsoft');
    }
  }, [accountConnections, isConnectionsLoading]);

  const filterSelectedEvents = useFilterSelectedEvents();
  const mergeEventsWithDelta = useMergeEventsWithDelta();
  const { handleLoadMore } = useLoadMoreCalendarEvents({
    accountConnections,
    activeTab,
    googleNextPageToken,
    microsoftNextLink,
    setGoogleEvents,
    setMicrosoftEvents,
    setGoogleNextPageToken,
    setMicrosoftNextLink,
    setIsLoadingMoreGoogle,
    setIsLoadingMoreMicrosoft,
    googleSyncTokenRef,
    microsoftDeltaLinkRef,
    reportCalendarAuthError,
  });
  const { syncCalendarIncrementally } = useIncrementalCalendarSync({
    accountConnections,
    activeTab,
    googleSyncTokenRef,
    microsoftDeltaLinkRef,
    setGoogleEvents,
    setMicrosoftEvents,
    mergeEventsWithDelta,
    reportCalendarAuthError,
  });

  // Fetch Google Events
  const {
    data: googleFirstPage,
    isLoading: isLoadingGoogle,
    isError: isErrorGoogle,
  } = useQuery<{
    events: CalendarEvent[];
    nextPageToken?: string;
    nextSyncToken?: string;
  }>({
    queryKey: ['googleEvents', accountConnections, activeTab],
    enabled:
      !!accountConnections?.google?.connected &&
      (!googleCacheRef.current[activeTab] || !!googleCacheRef.current[activeTab]?.isBackfilled),
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      const google = accountConnections?.google;
      if (!google) {
        return { events: [], nextPageToken: undefined };
      }

      const page = await calendarService.fetchGoogleCalendarEventsList(google, activeTab);
      reportCalendarAuthError('google', page.authError);

      const events = (page.events ?? []) as CalendarEvent[];

      return {
        events: events.map((e) => ({
          ...e,
          provider: 'google',
        })),
        nextPageToken: page.nextPageToken,
        nextSyncToken: page.nextSyncToken,
      };
    },
  });

  // Fetch Microsoft Events
  const {
    data: microsoftFirstPage,
    isLoading: isLoadingMs,
    isError: isErrorMs,
  } = useQuery<{
    events: CalendarEvent[];
    nextLink?: string;
    deltaLink?: string;
  }>({
    queryKey: ['microsoftEvents', accountConnections, activeTab],
    enabled:
      !!accountConnections?.microsoft?.connected &&
      (!microsoftCacheRef.current[activeTab] ||
        !!microsoftCacheRef.current[activeTab]?.isBackfilled),
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      const microsoft = accountConnections?.microsoft;
      if (!microsoft) {
        return { events: [], nextLink: undefined };
      }

      const page = await calendarService.fetchMicrosoftCalendarEventsList(microsoft, activeTab);
      reportCalendarAuthError('microsoft', page.authError);

      const events = (page.events ?? []) as CalendarEvent[];

      return {
        events: events.map((e) => withMicrosoftProvider(e)),
        nextLink: page.nextLink,
        deltaLink: page.deltaLink,
      };
    },
  });

  // Fetch Google event & calendar colors
  // biome-ignore lint/suspicious/noExplicitAny: Google colors response is an untyped API payload
  const { data: googleColors } = useQuery<any>({
    queryKey: ['googleColors', accountConnections?.google?.login_link],
    enabled: !!accountConnections?.google?.connected,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      const googleAccessToken = accountConnections?.google?.login_link;
      if (!googleAccessToken) {
        return null;
      }
      return calendarService.fetchGoogleCalendarColors(googleAccessToken);
    },
  });

  // Fetch Microsoft master categories (color metadata)
  // biome-ignore lint/suspicious/noExplicitAny: Microsoft categories response is an untyped API payload
  const { data: microsoftCategories } = useQuery<any[]>({
    queryKey: ['microsoftCategories', accountConnections?.microsoft?.login_link],
    enabled: !!accountConnections?.microsoft?.connected,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      const microsoftAccessToken = accountConnections?.microsoft?.login_link;
      if (!microsoftAccessToken) {
        return [];
      }
      const categories = await calendarService.fetchMicrosoftCategories(microsoftAccessToken);
      return categories ?? [];
    },
  });

  // Sync first page query results into local pagination state
  useEffect(() => {
    if (!activeTab) {
      return;
    }

    if (googleCacheRef.current[activeTab] && !googleCacheRef.current[activeTab]?.isBackfilled) {
      return;
    }

    if (googleFirstPage) {
      setGoogleEvents(googleFirstPage.events ?? []);
      setGoogleNextPageToken(googleFirstPage.nextPageToken);
      googleCacheRef.current[activeTab] = {
        events: googleFirstPage.events ?? [],
        nextPageToken: googleFirstPage.nextPageToken,
        isBackfilled: false,
      };
      if (googleFirstPage.nextSyncToken) {
        googleSyncTokenRef.current[activeTab] = googleFirstPage.nextSyncToken;
      }
    } else {
      setGoogleEvents([]);
      setGoogleNextPageToken(undefined);
    }
  }, [googleFirstPage, activeTab]);

  useEffect(() => {
    if (!activeTab) {
      return;
    }

    if (
      microsoftCacheRef.current[activeTab] &&
      !microsoftCacheRef.current[activeTab]?.isBackfilled
    ) {
      return;
    }

    if (microsoftFirstPage) {
      setMicrosoftEvents(microsoftFirstPage.events ?? []);
      setMicrosoftNextLink(microsoftFirstPage.nextLink);
      microsoftCacheRef.current[activeTab] = {
        events: microsoftFirstPage.events ?? [],
        nextLink: microsoftFirstPage.nextLink,
        isBackfilled: false,
      };
      if (microsoftFirstPage.deltaLink) {
        microsoftDeltaLinkRef.current[activeTab] = microsoftFirstPage.deltaLink;
      }
    } else {
      setMicrosoftEvents([]);
      setMicrosoftNextLink(undefined);
    }
  }, [microsoftFirstPage, activeTab]);

  useEffect(() => {
    if (!activeTab) {
      return;
    }
    if (previousTabRef.current === activeTab) {
      return;
    }

    const previousTab = previousTabRef.current;

    if (previousTab) {
      googleCacheRef.current[previousTab] = {
        events: googleEvents,
        nextPageToken: googleNextPageToken,
        isBackfilled: googleCacheRef.current[previousTab]?.isBackfilled,
      };
      microsoftCacheRef.current[previousTab] = {
        events: microsoftEvents,
        nextLink: microsoftNextLink,
        isBackfilled: microsoftCacheRef.current[previousTab]?.isBackfilled,
      };
    }

    const cachedGoogle = googleCacheRef.current[activeTab];
    if (cachedGoogle) {
      setGoogleEvents(cachedGoogle.events ?? []);
      setGoogleNextPageToken(cachedGoogle.nextPageToken);
    }

    const cachedMicrosoft = microsoftCacheRef.current[activeTab];
    if (cachedMicrosoft) {
      setMicrosoftEvents(cachedMicrosoft.events ?? []);
      setMicrosoftNextLink(cachedMicrosoft.nextLink);
    }

    previousTabRef.current = activeTab;
  }, [activeTab, googleEvents, googleNextPageToken, microsoftEvents, microsoftNextLink]);

  const mergeUniqueEvents = useCallback((events: CalendarEvent[]): CalendarEvent[] => {
    const eventMap = new Map<string, CalendarEvent>();
    for (const event of events) {
      const eventKey = `${event.provider ?? 'unknown'}:${event.id ?? ''}:${
        event.start?.dateTime ?? event.start?.date ?? ''
      }`;
      eventMap.set(eventKey, event);
    }
    return Array.from(eventMap.values());
  }, []);

  type EventCacheView = {
    current: Record<string, { events: CalendarEvent[]; isBackfilled?: boolean } | undefined>;
  };

  type BackfillCacheView = {
    current: Record<
      string,
      ({ events: CalendarEvent[]; isBackfilled?: boolean } & Record<string, unknown>) | undefined
    >;
  };

  const getKnownEventsFromTabCaches = useCallback(
    (events: CalendarEvent[], cache: EventCacheView): CalendarEvent[] => {
      return mergeUniqueEvents([
        ...events,
        ...(cache.current.upcoming?.events ?? []),
        ...(cache.current.past?.events ?? []),
      ]);
    },
    [mergeUniqueEvents],
  );

  const backfillOppositeTabCache = useCallback(
    (
      cache: BackfillCacheView,
      currentEvents: CalendarEvent[],
      oppositeTab: 'upcoming' | 'past',
    ) => {
      const existingOpposite = cache.current[oppositeTab];
      const knownEvents = mergeUniqueEvents([
        ...currentEvents,
        ...(existingOpposite?.events ?? []),
      ]);
      if (knownEvents.length > 0 || existingOpposite?.isBackfilled) {
        cache.current[oppositeTab] = {
          ...existingOpposite,
          events: filterEventsByTime(knownEvents, oppositeTab),
          isBackfilled: existingOpposite?.isBackfilled ?? true,
        };
      }
    },
    [mergeUniqueEvents],
  );

  // Backfill the opposite tab from already loaded events so we can switch tabs
  // without additional API calls until refresh.
  useEffect(() => {
    void timeBoundaryTick;
    if (activeTab !== 'upcoming') {
      return;
    }
    backfillOppositeTabCache(googleCacheRef, googleEvents, 'past');
    backfillOppositeTabCache(microsoftCacheRef, microsoftEvents, 'past');
  }, [activeTab, backfillOppositeTabCache, googleEvents, microsoftEvents, timeBoundaryTick]);

  // Trigger a re-render exactly when the next event crosses its end-time boundary.
  useEffect(() => {
    void timeBoundaryTick;
    const now = DateTime.local();
    const knownGoogleEvents = getKnownEventsFromTabCaches(googleEvents, googleCacheRef);
    const knownMicrosoftEvents = getKnownEventsFromTabCaches(microsoftEvents, microsoftCacheRef);
    const nextBoundary = [...knownGoogleEvents, ...knownMicrosoftEvents]
      .map((event) => {
        const endIso =
          event.end?.dateTime ?? event.end?.date ?? event.start?.dateTime ?? event.start?.date;
        const eventTimeZone = event.start?.timeZone ?? event.end?.timeZone ?? undefined;
        return toLocalDateTime(endIso, eventTimeZone);
      })
      .filter((dt) => dt.isValid && dt > now)
      .sort((a, b) => a.toMillis() - b.toMillis())[0];

    if (!nextBoundary) {
      return;
    }

    const msUntilBoundary = Math.max(0, nextBoundary.toMillis() - now.toMillis()) + 250;
    const safeDelayMs = Math.min(msUntilBoundary, 2_147_483_647);
    const timeout = window.setTimeout(() => {
      setTimeBoundaryTick(Date.now());
    }, safeDelayMs);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [getKnownEventsFromTabCaches, googleEvents, microsoftEvents, timeBoundaryTick]);

  // When the window/tab regains focus, refresh local time-based grouping immediately.
  useEffect(() => {
    const refreshByFocus = () => {
      setTimeBoundaryTick(Date.now());
    };
    const refreshByVisibility = () => {
      if (!document.hidden) {
        setTimeBoundaryTick(Date.now());
      }
    };

    window.addEventListener('focus', refreshByFocus);
    document.addEventListener('visibilitychange', refreshByVisibility);

    return () => {
      window.removeEventListener('focus', refreshByFocus);
      document.removeEventListener('visibilitychange', refreshByVisibility);
    };
  }, []);

  // Merge & Filter Events
  const allEvents = useMemo(() => {
    void timeBoundaryTick;
    // Only ever surface events for currently connected providers: the caches
    // outlive a disconnect by a render or two, and both providers' events are
    // rendered from the same list.
    const knownGoogleEvents = accountConnections?.google?.connected
      ? getKnownEventsFromTabCaches(googleEvents, googleCacheRef)
      : [];
    const knownMicrosoftEvents = accountConnections?.microsoft?.connected
      ? getKnownEventsFromTabCaches(microsoftEvents, microsoftCacheRef)
      : [];
    const combined = [...knownGoogleEvents, ...knownMicrosoftEvents];
    if (!combined.length) {
      return [];
    }

    const filtered = combined.filter((event) => {
      const title = event.summary || event.subject || '';
      return title.toLowerCase().includes(searchInput.toLowerCase());
    });

    const myEmails = [
      accountConnections?.google?.email,
      accountConnections?.microsoft?.email,
    ].filter((email): email is string => typeof email === 'string');

    return filterSelectedEvents(filtered, filterInput, myEmails);
  }, [
    googleEvents,
    microsoftEvents,
    searchInput,
    filterInput,
    filterSelectedEvents,
    getKnownEventsFromTabCaches,
    timeBoundaryTick,
    accountConnections?.google?.connected,
    accountConnections?.google?.email,
    accountConnections?.microsoft?.connected,
    accountConnections?.microsoft?.email,
  ]);

  const groupedEvents = useMemo(() => {
    void timeBoundaryTick;
    if (!allEvents.length) {
      return [];
    }

    const isUpcoming = activeTab === 'upcoming';

    const filteredByTime = filterEventsByTime(
      allEvents,
      activeTab === 'upcoming' ? 'upcoming' : 'past',
    );

    const sorted = [...filteredByTime].sort((a, b) => {
      const dateA = getEventLocalStart(a).toMillis();
      const dateB = getEventLocalStart(b).toMillis();
      return isUpcoming ? dateA - dateB : dateB - dateA;
    });

    const groups: Record<string, CalendarEvent[]> = {};

    for (const ev of sorted) {
      const startDateTime = ev.start?.dateTime ?? ev.start?.date ?? '';
      const key = getLocalDateKey(startDateTime, ev.start?.timeZone);
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(ev);
    }

    return Object.entries(groups).sort((a, b) => {
      const keyA = a[0];
      const keyB = b[0];
      return isUpcoming ? keyA.localeCompare(keyB) : keyB.localeCompare(keyA);
    });
  }, [allEvents, activeTab, timeBoundaryTick]);

  const automaticColorsByEventKey = useMemo(() => {
    const eventColorMap: Record<string, { backgroundColor?: string; borderLeftColor?: string }> =
      {};
    let sequenceIndex = 0;

    for (const [, group] of groupedEvents) {
      for (const event of group) {
        const eventKey = `${event.provider ?? 'unknown'}:${event.id ?? ''}`;
        const hasExplicitColor = hasExplicitEventColor(event, googleColors, microsoftCategories);

        if (!hasExplicitColor) {
          eventColorMap[eventKey] =
            automaticEventColorCycle[sequenceIndex % automaticEventColorCycle.length];
        }

        sequenceIndex += 1;
      }
    }

    return eventColorMap;
  }, [groupedEvents, googleColors, microsoftCategories]);

  // Clean up copy-confirmation timer on unmount
  useEffect(() => {
    return () => {
      if (copiedTimerRef.current) {
        clearTimeout(copiedTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isConnectionsLoading || !accountConnections) {
      return;
    }

    if (accountConnections?.google?.connected) {
      setProvider('google');
      setIsConnected(true);
      onEventsProvider(true);
    } else if (accountConnections?.microsoft?.connected) {
      setProvider('microsoft');
      setIsConnected(true);
      onEventsProvider(true);
    } else {
      setProvider(null);
      setIsConnected(false);
      onEventsProvider(false);
    }
  }, [accountConnections, isConnectionsLoading, onEventsProvider]);

  const providers = Object.entries(accountConnections || {});
  const isAnyProviderConnected =
    !!accountConnections?.google?.connected || !!accountConnections?.microsoft?.connected;
  const isHosted = isHostedByTeamHub();
  const hasBackfilledGoogleEvents =
    !!googleCacheRef.current[activeTab]?.isBackfilled && googleEvents.length > 0;
  const hasBackfilledMicrosoftEvents =
    !!microsoftCacheRef.current[activeTab]?.isBackfilled && microsoftEvents.length > 0;
  const hasBackfilledEvents = hasBackfilledGoogleEvents || hasBackfilledMicrosoftEvents;
  const isProviderInitialLoading =
    !!accountConnections &&
    ((!!accountConnections?.google?.connected && isLoadingGoogle) ||
      (!!accountConnections?.microsoft?.connected && isLoadingMs));
  const isEventsLoading = isProviderInitialLoading && !hasBackfilledEvents;
  const hasVisibleEvents = groupedEvents.length > 0;
  const isTabSwitching =
    previousTabRef.current !== undefined && previousTabRef.current !== activeTab;
  const emptyStateMessage = activeTab === 'past' ? 'No past events.' : 'No upcoming events.';
  const hasMoreEvents = !!googleNextPageToken || !!microsoftNextLink;
  const isListStable = !(isEventsLoading || isTabSwitching);
  const shouldShowEmptyState = isListStable && !hasVisibleEvents;
  const shouldShowLoadMore = isListStable && hasVisibleEvents && hasMoreEvents;

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (!isTrustedCalendarMessageOrigin(event.origin)) {
        return;
      }
      const { type, provider, success } = event.data || {};
      if ((type === 'calendar-connected' || type === 'calendar-disconnected') && success) {
        if (provider === 'google' || provider === 'microsoft') {
          authErrorShownRef.current[provider as 'google' | 'microsoft'] = false;
          if (provider === 'google') {
            setGoogleReconnectError(null);
          }
          sessionStorage.removeItem(`calendarReconnectError:${provider}`);
          if (type === 'calendar-disconnected') {
            // Drop the events now rather than after the connections refetch
            // resolves, so they never linger next to another provider's list.
            resetProviderEvents(provider);
          }
        }
        console.log(
          `${provider} ${type === 'calendar-connected' ? 'connected' : 'disconnected'}! Refreshing data...`,
        );
        if (type === 'calendar-connected') {
          onEventsProvider(true);
        }
        refetch();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [refetch, onEventsProvider, resetProviderEvents]);

  useEffect(() => {
    const handleScheduledMeetingSuccess = (
      event: Event & { detail?: { createdCalendarEvent?: CreatedCalendarEvent } },
    ) => {
      const createdCalendarEvent = event.detail?.createdCalendarEvent;
      void (async () => {
        await syncCalendarIncrementally();
        setPendingCreatedCalendarEvent(createdCalendarEvent);
      })();
    };
    window.addEventListener('calendar-meeting-scheduled', handleScheduledMeetingSuccess);
    return () => {
      window.removeEventListener('calendar-meeting-scheduled', handleScheduledMeetingSuccess);
    };
  }, [syncCalendarIncrementally]);

  useEffect(() => {
    if (
      !(
        pendingCreatedCalendarEvent?.id &&
        pendingCreatedCalendarEvent.provider &&
        accountConnections
      )
    ) {
      return;
    }
    const targetEvents =
      pendingCreatedCalendarEvent.provider === 'google' ? googleEvents : microsoftEvents;
    const isVisible = targetEvents.some((event) => event.id === pendingCreatedCalendarEvent.id);
    if (isVisible) {
      setPendingCreatedCalendarEvent(undefined);
      return;
    }
    if (autoLoadInProgressRef.current) {
      return;
    }

    if (pendingCreatedCalendarEvent.provider === 'google') {
      if (!(googleNextPageToken && accountConnections.google?.connected)) {
        setPendingCreatedCalendarEvent(undefined);
        return;
      }
      autoLoadInProgressRef.current = true;
      void (async () => {
        try {
          const page = await calendarService.fetchGoogleCalendarEventsList(
            accountConnections.google,
            activeTab,
            googleNextPageToken,
          );
          reportCalendarAuthError('google', page.authError);
          const mappedEvents = ((page.events ?? []) as CalendarEvent[]).map((event) => ({
            ...event,
            provider: 'google',
          }));
          setGoogleEvents((prev) => [...prev, ...mappedEvents]);
          setGoogleNextPageToken(page.nextPageToken);
          if (page.nextSyncToken) {
            googleSyncTokenRef.current[activeTab] = page.nextSyncToken;
          }
        } finally {
          autoLoadInProgressRef.current = false;
        }
      })();
      return;
    }

    if (!(microsoftNextLink && accountConnections.microsoft?.connected)) {
      setPendingCreatedCalendarEvent(undefined);
      return;
    }
    autoLoadInProgressRef.current = true;
    void (async () => {
      try {
        const page = await calendarService.fetchMicrosoftCalendarEventsList(
          accountConnections.microsoft,
          activeTab,
          microsoftNextLink,
        );
        reportCalendarAuthError('microsoft', page.authError);
        const mappedEvents = ((page.events ?? []) as CalendarEvent[]).map((event) =>
          withMicrosoftProvider(event),
        );
        setMicrosoftEvents((prev) => [...prev, ...mappedEvents]);
        setMicrosoftNextLink(page.nextLink);
        if (page.deltaLink) {
          microsoftDeltaLinkRef.current[activeTab] = page.deltaLink;
        }
      } finally {
        autoLoadInProgressRef.current = false;
      }
    })();
  }, [
    accountConnections,
    activeTab,
    googleEvents,
    googleNextPageToken,
    microsoftEvents,
    microsoftNextLink,
    pendingCreatedCalendarEvent,
    reportCalendarAuthError,
  ]);

  const eventTabRefreshKey = useMemo(
    () =>
      JSON.stringify(
        [...googleEvents, ...microsoftEvents].map((event) => {
          const rawEvent = event as CalendarEvent & { status?: string; isCancelled?: boolean };
          return {
            id: rawEvent.id,
            provider: rawEvent.provider,
            start: rawEvent.start?.dateTime ?? rawEvent.start?.date ?? '',
            end: rawEvent.end?.dateTime ?? rawEvent.end?.date ?? '',
            status: rawEvent.status ?? '',
            isCancelled: rawEvent.isCancelled ?? false,
            summary: rawEvent.summary ?? '',
            subject: rawEvent.subject ?? '',
          };
        }),
      ),
    [googleEvents, microsoftEvents],
  );

  useEffect(() => {
    if (!eventTabRefreshKey) {
      return;
    }
    void queryClient.invalidateQueries({ queryKey: ['eventTabGoogleEvents'] });
    void queryClient.invalidateQueries({ queryKey: ['eventTabMicrosoftEvents'] });
  }, [eventTabRefreshKey, queryClient]);

  const handleSignIn = (_e: ReactMouseEvent<HTMLButtonElement>, key: string, loginLink: string) => {
    let win: Window | null = null;
    const provider = key === 'google' || key === 'microsoft' ? (key as CalendarProvider) : null;
    if (provider) {
      win = window.open(
        `${loginLink}${buildCalendarOauthCloseUrl(provider)}`,
        '_blank',
        'height=580,width=800',
      );
      console.log(`Connecting to ${key.charAt(0).toUpperCase() + key.slice(1)} Calendar...`);
      setIsConnecting(true);
    }

    const interval = setInterval(() => {
      if (win?.closed) {
        clearInterval(interval);
        setIsConnecting(false);
      }
    }, 1000);
  };

  const handleOpenCalendarIntegrationSettings = () => {
    openCalendarIntegrations();
  };

  const isEventOwnedByCurrentUser = (event: CalendarEvent) => {
    const organizerEmail = getOrganizerEmail(event);
    const myEmails = [
      accountConnections?.google?.email,
      accountConnections?.microsoft?.email,
    ].filter((email): email is string => typeof email === 'string');
    return myEmails.includes(organizerEmail);
  };

  const canEdit = (event: CalendarEvent) => {
    return isEventOwnedByCurrentUser(event) || event.provider === 'google';
  };

  const getMeetingLink = async (
    _event: ReactMouseEvent<HTMLButtonElement>,
    ev: CalendarEvent,
  ): Promise<void> => {
    // Create the room as a "standard" meeting, matching what Schedule Meeting flow creates
    const userDomain = userEmail?.split('@')[1] || '';
    const meetingRequest: CreateMeetingParams = {
      description: null,
      password: '',
      ...createMeetingAccessPayload('standard', userDomain),
    };
    let createErrorMessage: string | undefined;
    const result = await create(meetingRequest).catch((err) => {
      console.error('[ERROR] Failed to generate meeting code', err);
      createErrorMessage = err instanceof Error ? err.message : 'Failed to generate meeting code.';
      return undefined;
    });

    if (!result) {
      const errorMsg = `Could not generate a meeting link: ${createErrorMessage ?? 'Unknown error.'}`;
      onCalendarUpdateMeetingError?.(errorMsg);
      return;
    }

    const room = result.room ?? '';
    const password = result.password ?? '';

    if (!room) {
      const providerName = ev.provider === 'google' ? 'Google' : 'Microsoft';
      const errorMsg = `Could not update ${providerName} calendar event: meeting room was not allocated.`;
      onCalendarUpdateMeetingError?.(errorMsg);
      console.error('[ERROR] Meeting was created without a room name; aborting calendar update.');
      return;
    }

    const locationLink = getMeetingUrl(room, password);
    const providerName = ev.provider === 'google' ? 'Google' : 'Microsoft';

    try {
      switch (ev.provider) {
        case 'google': {
          const googleAccessToken = accountConnections?.google?.login_link;
          if (!googleAccessToken) {
            return;
          }
          ev.location = locationLink;
          await calendarService.updateGoogleEventWithMeetLink(googleAccessToken, {
            ...ev,
            calendarId: ev.calendarId ?? accountConnections?.google?.email ?? 'primary',
          });
          queryClient.invalidateQueries({ queryKey: ['googleEvents'] });
          queryClient.invalidateQueries({ queryKey: ['eventTabGoogleEvents'] });
          break;
        }
        case 'microsoft': {
          const microsoftAccessToken = accountConnections?.microsoft?.login_link;
          if (!microsoftAccessToken) {
            return;
          }
          ev.location = {
            displayName: locationLink,
            locationType: 'default',
            uniqueId: locationLink,
            uniqueIdType: 'private',
          };
          await calendarService.updateMicrosoftEventWithMeetLink(microsoftAccessToken, ev);
          queryClient.invalidateQueries({ queryKey: ['microsoftEvents'] });
          queryClient.invalidateQueries({ queryKey: ['eventTabMicrosoftEvents'] });
          break;
        }
      }
    } catch (err) {
      // Calendar update failed: clean up the orphaned room so we don't leak
      // unattached schedules onto the backend on every retry.
      await remove(room).catch((removeErr) => {
        console.error(
          '[ERROR] Failed to clean up meeting after calendar update failure:',
          removeErr,
        );
      });

      const message =
        err instanceof Error ? err.message : 'Failed to update event location with meeting link.';
      const errorMsg = `Could not update ${providerName} calendar event: ${message}`;
      onCalendarUpdateMeetingError?.(errorMsg);
      console.error('[ERROR] Update event location failed:', err);
    }
  };

  if (isErrorConnections || isErrorGoogle || isErrorMs) {
    return (
      <div
        role="alert"
        style={{
          minHeight: '50vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          textAlign: 'center',
        }}
      >
        <div style={{ maxWidth: '460px' }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: 600 }}>
            Unable to load calendar data
          </h3>
          <p style={{ margin: 0, color: '#5f6368', lineHeight: 1.5 }}>
            Please reload the page. If the issue persists, sign out and sign back in to your
            calendar account.
          </p>
          <button
            type="button"
            className="loadMoreButton"
            style={{ marginTop: '16px' }}
            onClick={() => window.location.reload()}
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="calendarRecentPanel">
      {/* Calendar Content - No tabs, just calendar */}
      <div className="calendarContent">
        {isAnyProviderConnected && isConnected && googleReconnectError && (
          <div className="calendarReconnectNotice" role="alert">
            <span>{googleReconnectError}</span>
            <button
              type="button"
              className="calendarReconnectLink"
              onClick={handleOpenCalendarIntegrationSettings}
            >
              Open Calendar Integration settings
            </button>
          </div>
        )}

        {!(isAnyProviderConnected || isConnectionsLoading) &&
          (isHosted || providers.length > 0) && (
            <div className="calendarConnectContainer">
              <div className="calendarConnectIcon">
                <img
                  className="calendarConnectIcon--light"
                  src={calendarPlusIcon.src}
                  width={calendarPlusIcon.width}
                  height={calendarPlusIcon.height}
                  alt=""
                />
                <img
                  className="calendarConnectIcon--dark"
                  src={calendarPlusIconDark.src}
                  width={calendarPlusIconDark.width}
                  height={calendarPlusIconDark.height}
                  alt=""
                />
              </div>
              <h3 className="calendarConnectTitle">
                Connect your calendar to view all your meetings
              </h3>
              <p className="calendarConnectDescription">
                Securely connect your Google or Microsoft calendar to seamlessly access and manage
                your events{isHosted ? ' directly from Settings' : ''}.
              </p>

              <div className="calendarConnectButtons">
                {isHosted ? (
                  <button
                    type="button"
                    className="calendarConnectButton"
                    onClick={handleOpenCalendarIntegrationSettings}
                    disabled={isConnectionsFetching}
                  >
                    Connect calendars
                  </button>
                ) : (
                  providers.map(([provider, data]) => (
                    <button
                      type="button"
                      key={provider}
                      className={`calendarConnectButton ${
                        provider === 'google'
                          ? 'googleButton'
                          : provider === 'microsoft'
                            ? 'microsoftButton'
                            : ''
                      }`}
                      onClick={(e) => handleSignIn(e, provider, data?.login_link)}
                      disabled={isConnecting || isConnectionsFetching}
                    >
                      <img
                        src={
                          provider === 'google'
                            ? gcalendarIcon.src
                            : provider === 'microsoft'
                              ? outlookIcon.src
                              : ''
                        }
                        width={
                          provider === 'google'
                            ? gcalendarIcon.width
                            : provider === 'microsoft'
                              ? outlookIcon.width
                              : undefined
                        }
                        height={
                          provider === 'google'
                            ? gcalendarIcon.height
                            : provider === 'microsoft'
                              ? outlookIcon.height
                              : undefined
                        }
                        alt={provider}
                        className="buttonIcon"
                      />
                      Connect with{' '}
                      {provider.charAt(0).toUpperCase() + provider.slice(1).toLowerCase()}
                    </button>
                  ))
                )}
              </div>
              {error && <div className="calendarConnectError">{error}</div>}
            </div>
          )}

        {isAnyProviderConnected && isConnected && (
          <div className="calendarList">
            <div className="calendarItems">
              {isAnyProviderConnected && isEventsLoading ? (
                <div className="calendar-loading-placeholder" aria-live="polite">
                  {[1, 2, 3, 4, 5].map((rowId) => (
                    <div key={`calendar-loading-row-${rowId}`} className="calendar-loading-row" />
                  ))}
                </div>
              ) : (
                <>
                  {groupedEvents?.map(([dateKey, group]) => (
                    <div key={dateKey} className="calendarDayGroup">
                      {group.map((ev, idx) => {
                        const startDateTime = ev.start?.dateTime ?? ev.start?.date ?? '';
                        const endDateTime = ev.end?.dateTime ?? ev.end?.date ?? '';
                        const isAllDay = ev.isAllDay || !!ev.start?.date;

                        const userTimeZone = DateTime.local().zoneName;
                        const timezone = ev.start?.timeZone ?? userTimeZone;

                        const [year, month, day] = getLocalDateKey(startDateTime, timezone)
                          .split('-')
                          .map(Number);

                        // month is 0-based in JS Date
                        const dateObj = new Date(year, month - 1, day);

                        const dayName = dateObj
                          .toLocaleDateString(undefined, {
                            weekday: 'short',
                          })
                          .toUpperCase();

                        const dayNumber = dateObj.getDate();

                        const timeLabel = formatEventTime(
                          startDateTime,
                          endDateTime,
                          timezone,
                          userTimeZone,
                          isAllDay,
                        );

                        const locationLink = getLocationLink(ev);
                        const eventKey = `${ev.provider ?? 'unknown'}:${ev.id ?? ''}`;
                        const fallbackAutomaticColors = automaticColorsByEventKey[eventKey];
                        const eventColors = getEventColors(
                          ev,
                          googleColors,
                          microsoftCategories,
                          fallbackAutomaticColors,
                        );
                        const editProvider =
                          ev.provider === 'google' || ev.provider === 'microsoft'
                            ? ev.provider
                            : undefined;
                        const editHtmlLink =
                          'htmlLink' in ev && typeof ev.htmlLink === 'string'
                            ? ev.htmlLink
                            : undefined;

                        return (
                          <div
                            key={ev.id}
                            className={`dateEventBox ${idx === 0 ? 'withDate' : ''}`}
                          >
                            <div className="dateBlock">
                              <div className="dateText">{dayName}</div>
                              <div className="dateNumber">{dayNumber}</div>
                            </div>
                            <div
                              className={`calendarItem ${activeTab === 'upcoming' ? 'opacity-100' : 'opacity-60'}`}
                              style={
                                {
                                  ...eventColors,
                                  '--event-accent-color': eventColors.borderLeftColor,
                                } as CSSProperties
                              }
                            >
                              <div className="itemMain">
                                <div className="itemTitle">{ev.summary || ev.subject}</div>

                                <div className="itemTime">
                                  {timeLabel}
                                  {locationLink && (
                                    <>
                                      {' at '}
                                      <a
                                        href={locationLink}
                                        rel="noopener noreferrer"
                                        className="locationLink"
                                        onClick={(event) => {
                                          if (!isPlainLeftClick(event)) {
                                            return;
                                          }
                                          event.preventDefault();
                                          openMeetingLink(locationLink);
                                        }}
                                      >
                                        {locationLink}
                                      </a>
                                    </>
                                  )}
                                </div>
                              </div>

                              {ev?.attendees && (
                                <div className="avatarGroup">
                                  {ev.attendees
                                    .slice(0, 3)
                                    .map((attendee: CalendarAttendee, index: number) => {
                                      const attendeeImage = getAttendeeImage(attendee);
                                      return (
                                        <div
                                          key={`attendee-${attendee?.email ?? index}`}
                                          className="itemAvatar"
                                        >
                                          {attendeeImage ? (
                                            <img
                                              src={attendeeImage ?? ''}
                                              alt=""
                                              width={40}
                                              height={40}
                                            />
                                          ) : (
                                            getOrganizerOrAttendee(attendee)
                                          )}
                                        </div>
                                      );
                                    })}
                                  {ev.attendees.length > 3 && (
                                    <div className="itemAvatar more">
                                      <span className="more-text">
                                        <span className="plus-icon">+ </span>
                                        {ev.attendees.length - 3} more
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )}

                              {activeTab === 'upcoming' && (
                                <div className="itemActions">
                                  {locationLink && (
                                    <button
                                      type="button"
                                      className="iconButton"
                                      aria-label={copiedEventId === ev.id ? 'Copied' : 'Copy link'}
                                      onClick={() => {
                                        navigator.clipboard?.writeText(locationLink || '');
                                        setCopiedEventId(ev.id);
                                        if (copiedTimerRef.current) {
                                          clearTimeout(copiedTimerRef.current);
                                        }
                                        copiedTimerRef.current = setTimeout(() => {
                                          setCopiedEventId(null);
                                          copiedTimerRef.current = null;
                                        }, 2000);
                                      }}
                                    >
                                      <img
                                        src={
                                          copiedEventId === ev.id
                                            ? checkIconGreen.src
                                            : copyIcon.src
                                        }
                                        width={
                                          copiedEventId === ev.id
                                            ? checkIconGreen.width
                                            : copyIcon.width
                                        }
                                        height={
                                          copiedEventId === ev.id
                                            ? checkIconGreen.height
                                            : copyIcon.height
                                        }
                                        alt=""
                                      />
                                    </button>
                                  )}

                                  {!locationLink && isEventOwnedByCurrentUser(ev) && (
                                    <button
                                      type="button"
                                      className="iconButton"
                                      onClick={(event) => getMeetingLink(event, ev)}
                                      aria-label="Add link"
                                    >
                                      <img
                                        src={plusIcon.src}
                                        width={plusIcon.width}
                                        height={plusIcon.height}
                                        alt=""
                                      />
                                    </button>
                                  )}
                                  {canEdit(ev) && (
                                    <button
                                      type="button"
                                      className="iconButton"
                                      aria-label="Edit"
                                      onClick={() =>
                                        redirectToCalendarEdit(
                                          {
                                            id: ev.id,
                                            calendarId: ev.calendarId,
                                            provider: editProvider,
                                            htmlLink: editHtmlLink,
                                          },
                                          accountConnections?.microsoft?.login_link,
                                          accountConnections?.microsoft?.email,
                                        )
                                      }
                                    >
                                      <img
                                        src={editIcon.src}
                                        width={editIcon.width}
                                        height={editIcon.height}
                                        alt=""
                                      />
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                  {shouldShowEmptyState && <div className="emptyMessage">{emptyStateMessage}</div>}
                </>
              )}
            </div>

            {shouldShowLoadMore && (
              <div className="loadMoreContainer">
                <button
                  type="button"
                  className="loadMoreButton"
                  onClick={handleLoadMore}
                  disabled={isLoadingMoreGoogle || isLoadingMoreMicrosoft}
                >
                  {isLoadingMoreGoogle || isLoadingMoreMicrosoft ? 'Loading...' : 'Load more'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export { CalendarRecentPanel };
